import React from 'react';
import {
  Typography,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Box,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import { tokens, withAlpha } from '../../Common/designTokens';
import { IntegerInput, FloatInput } from '../OrderEntry/NumericInputs';
import { inputStyle } from '../OrderEntry/styles';

const MAX_QTY = 1000000000;

const AddAccountFormContent = ({
  formData,
  accountIdentifiers,
  custodians,
  fieldErrors,
  rowErrors,
  rowFieldErrors,
  duplicateCusips,
  touchedFields,
  touchedRowFields,
  rowLoadingStates,
  isLoading,
  errorMessage,
  editMode,
  originalCusips = new Set(),
  handleAccountChange,
  handleCustodianChange,
  handleChange,
  handleRowChange,
  handleRowDelete,
  addRow,
  markFieldAsTouched,
  markRowFieldAsTouched,
  isCheckingAccount,
  isAccountIdentifierValidated,
  mappingMode = false,
  lockHeaderFields = false,
  hidePositions = false,
  lockAccountFieldOnly = false,
  positionsEditMode = false,
}) => {
  const effectiveEdit = editMode || mappingMode;
  const isFormLocked = !editMode && !isAccountIdentifierValidated;
  const disableAccountField = lockHeaderFields || lockAccountFieldOnly;
  const disableCustodianDesc = isLoading || isFormLocked || (lockHeaderFields && !lockAccountFieldOnly);

  // Function to check if field should show error
  const shouldShowError = (fieldName) => {
    return touchedFields[fieldName] && fieldErrors[fieldName];
  };

  // Function to check if row field should show error
  const shouldShowRowError = (rowIndex, fieldName) => {
    return (
      touchedRowFields[rowIndex]?.[fieldName] && 
      rowFieldErrors[rowIndex]?.[fieldName]
    );
  };

  // Function to get error message for a row field with priority
  const getRowFieldErrorMessage = (rowIndex, field) => {
    // Safety check for formData.rows
    if (!formData.rows || !formData.rows[rowIndex]) {
      return '';
    }
    
    // First check for row-specific validation errors
    if (shouldShowRowError(rowIndex, field)) {
      return rowFieldErrors[rowIndex][field];
    }
    
    // Then check for CUSIP-specific errors
    if (field === 'cusip') {
      const rowCusip = formData.rows[rowIndex].cusip;
      // Only show duplicate error if this row is not pendingDelete
      if (!formData.rows[rowIndex].pendingDelete && duplicateCusips.has(rowCusip ? rowCusip.toUpperCase() : '') && rowCusip) {
        return 'Duplicate CUSIP not allowed';
      }
      if (rowErrors[rowIndex]) {
        return rowErrors[rowIndex];
      }
    }
    
    return '';
  };

  // Determine if delete button should be shown (now show for all rows)
  const showDeleteButton = (index) => {
    if (!formData.rows || !formData.rows[index]) {
      return false;
    }
    return true;
  };

  // Update this function to check if this specific row is an original position
  // Not just if the CUSIP exists in original data
  const isExistingCusip = (row) => {
    return effectiveEdit && row.accountPositionMappingId && row.cusip && originalCusips.has(row.cusip);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      maxHeight: '70vh',
      overflow: 'hidden'
    }}>
      {/* Fixed Header Section */}
      <Box sx={{ 
        p: 3, 
        pb: 2,
        flexShrink: 0,
        borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`
      }}>
        {/* Enhanced error display for form validation errors */}
        {(fieldErrors.rows || fieldErrors.duplicateCusips) && (
          <Box sx={{ 
            mb: 2,
            p: 1.5,
            backgroundColor: withAlpha(tokens.palette.danger, 0.08),
            border: `1px solid ${withAlpha(tokens.palette.danger, 0.3)}`,
            borderRadius: '8px'
          }}>
            {fieldErrors.rows && (
              <Typography variant="body2" sx={{ color: tokens.palette.danger, textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                {fieldErrors.rows}
              </Typography>
            )}
            {fieldErrors.duplicateCusips && (
              <Typography variant="body2" sx={{ color: tokens.palette.danger, textAlign: 'center', fontWeight: 'bold', mb: 1 }}>
                {fieldErrors.duplicateCusips}
              </Typography>
            )}
          </Box>
        )}
        
        {/* Conditional Header Rendering */}
        {positionsEditMode ? (
          // NEW: Inline text mode (like AccountSummary) for positions edit
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            alignItems: { xs: 'flex-start', lg: 'center' },
            gap: '16px'
          }}>
            <Typography 
              variant="body2"
              sx={{
                color: tokens.colors.fontColour85,
                fontWeight: 700,
                fontSize: '14px',
                whiteSpace: 'pre-line'
              }}
            >
              Account #: {formData.accountGroup ? `ACC-${formData.accountGroup}` : '---'} &nbsp; | &nbsp;
              Account Description: {formData.description || '---'} &nbsp; | &nbsp;
              Custodian: {custodians.find(c => c.custodianMstId === formData.custodianId)?.custodianName || '---'}
            </Typography>
          </Box>
        ) : (
          // Form fields mode (existing behavior)
          <Grid container spacing={2}>
            <Grid 
              sx={{ 
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}  
              item xs={3.8}>
              {/* ACC- prefix */}
              <Box sx={{ 
                    color: editMode ? tokens.colors.fontColour50 : tokens.colors.fontColour85, 
                    py:"18.5px",
                    px: "10px",
                    fontSize: '16px',
                    fontWeight: 'medium',
                    borderLeft: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
                    backgroundColor: editMode ? tokens.colors.disabledWhite : tokens.colors.white,
                    borderColor: tokens.colors.bodyBorderColor50,
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                  }}>
                    ACC-
              </Box>
              <TextField
                fullWidth
                name="accountGroup"
                value={editMode ? formData.accountGroup.replace('ACC-', '') : formData.accountGroup}
                onChange={(e) => {
                  // In mapping mode, behave like create (numeric, editable)
                  if (!editMode) {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    handleAccountChange({...e, target: {...e.target, value: numericValue}});
                  } else {
                    const value = e.target.value.replace(/^ACC-/, '');
                    handleAccountChange({...e, target: {...e.target, value: value}});
                  }
                }}
                onKeyPress={(e) => {
                  if (!editMode && !/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                    e.preventDefault();
                  }
                }}
                variant="filled"
                label="Account (6 chars)"
                sx={{
                  ...inputStyle,
                }}
                InputProps={{ 
                  disableUnderline: true, 
                  // Keep account field editable in mapping mode
                  readOnly: disableAccountField || (editMode && !mappingMode),
                  inputMode: !editMode ? 'numeric' : 'text',
                  pattern: !editMode ? '[0-9]*' : undefined,
                  endAdornment: isCheckingAccount ? <CircularProgress size={20} /> : null
                }}
                error={shouldShowError('accountGroup')}
                disabled={isLoading || disableAccountField}
                placeholder="Enter id (e.g., 123456)"
                inputProps={{ maxLength: 6 }} // Always limit to 6 chars since ACC- is shown separately
              />
            </Grid>

            <Grid item xs={3.8}>
              <TextField
                fullWidth
                select
                name="custodianId"
                value={formData.custodianId || ''}
                onChange={handleCustodianChange}
                variant="filled"
                label="Custodian"
                sx={{
                  ...inputStyle,
                  // pl: "0px !important",
                  paddingLeft: "0px",
                }}
                InputProps={{ disableUnderline: true }}
                error={shouldShowError('custodianId')}
                //helperText={shouldShowError('custodianId') ? fieldErrors.custodianId : ''}
                disabled={disableCustodianDesc}
              >
                {custodians.map((option) => (
                  <MenuItem key={option.custodianMstId} value={option.custodianMstId}>
                    {option.custodianName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={3.8}>
              <TextField
                fullWidth
                name="description"
                value={formData.description}
                onChange={handleChange}
                variant="filled"
                label="Account Description"
                // make the field container relative so helper text can be absolutely positioned inside it
                sx={{
                  ...inputStyle,
                  position: 'relative',
                  // paddingRight: '48px' // give room so helper text doesn't overlap input content
                }}
                InputProps={{ disableUnderline: true }}
                error={shouldShowError('description')}
                helperText={
                  shouldShowError('description')
                  ? fieldErrors.description
                  : `${formData.description.length}/15 `
                }
                // position helper text at bottom-right inside the field
                FormHelperTextProps={{
                  sx: {
                    mt: 0,
                    mx: 0,
                    position: 'absolute',
                    right: '10px',
                    bottom: '4px',
                    margin: 0,
                    lineHeight: 1,
                    fontSize: '8px',
                    color: shouldShowError('description') ? tokens.palette.danger : tokens.colors.fontColour85
                  }
                }}
                placeholder="Enter account description (max 15 chars)"
                disabled={disableCustodianDesc}
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            {/* Empty grid item to match the delete button column in lower fields */}
            <Grid item xs={0.6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '56px' }}>
              {/* Empty space to align with delete button */}
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Dynamic CUSIP Rows Section */}
      {!hidePositions && (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flex: 1
      }}>
        {/* Scrollable Content */}
        <Box sx={{ 
          overflow: 'auto',
          px: 3,
          py: 2,
          flex: 1,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: tokens.colors.scrollbarTrack,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: tokens.colors.scrollbarThumb,
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: tokens.colors.fontColour25,
            },
          },
        }}>
          {/* Dynamic Rows */}
          {formData.rows && formData.rows.map((row, index) => {
            const isPendingDelete = !!row.pendingDelete;
            const isExistingRow = effectiveEdit && row.accountPositionMappingId;
            const canEditRow = !!row.secMasterId || isExistingRow;

            return (
              <Grid container spacing={2} key={index} sx={{ mb: 2.3, alignItems: 'center', opacity: isPendingDelete ? 0.5 : 1 }}>
                <Grid item xs={3.8}>
                  <TextField
                    fullWidth
                    value={row.cusip || ''}
                    onChange={(e) => handleRowChange(index, 'cusip', e.target.value)}
                    variant="filled"
                    label="SecurityId"
                    sx={{
                      ...inputStyle,
                      textDecoration: isPendingDelete ? 'line-through' : 'none',
                      textDecorationColor: isPendingDelete ? tokens.palette.danger : undefined,
                       textDecorationThickness: isPendingDelete ? '3px' : undefined,
                      color: isPendingDelete ? tokens.palette.danger : undefined,
                      '& .MuiFormHelperText-root': {
                        position: 'absolute',
                        bottom: '-14px',
                        margin: 0,
                        lineHeight: 1
                      }
                    }}
                    error={
                      (!isPendingDelete && (
                        duplicateCusips.has(row.cusip ? row.cusip.toUpperCase() : '') || 
                        !!rowErrors[index] || 
                        shouldShowRowError(index, 'cusip')
                      ))
                    }
                    FormHelperTextProps={{
                      sx: {
                        mt: 0,
                        mx: 0
                      }
                    }}
                    helperText={isPendingDelete ? '' : getRowFieldErrorMessage(index, 'cusip')}
                    InputProps={{ 
                      disableUnderline: true,
                      // Treat mapping like edit for existing rows
                      readOnly: (effectiveEdit && row.accountPositionMappingId) || isPendingDelete,
                      endAdornment: rowLoadingStates[index] ? <CircularProgress size={20} /> : null
                    }}
                    placeholder="Enter CUSIP"
                    disabled={isLoading || isExistingCusip(row) || isPendingDelete || isFormLocked}
                    onBlur={() => markRowFieldAsTouched(index, 'cusip')}
                  />
                </Grid>
                <Grid item xs={3.8}>
                  <IntegerInput
                    name={`quantity-${index}`}
                    value={row.quantity || ''}
                    onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                    placeholder="Enter quantity"
                    label="Quantity"
                    allowNegative={true} // <-- allow negative quantity
                    maxValue={MAX_QTY}
                    sx={{
                      ...inputStyle,
                      textDecoration: isPendingDelete ? 'line-through' : 'none',
                      textDecorationColor: isPendingDelete ? tokens.palette.danger : undefined,
                      textDecorationThickness: isPendingDelete ? '3px' : undefined,
                    }}
                    disabled={isLoading || !canEditRow || isPendingDelete || isFormLocked}
                    error={!isPendingDelete && shouldShowRowError(index, 'quantity')}
                    helperText={isPendingDelete ? '' : getRowFieldErrorMessage(index, 'quantity')}
                    onBlur={() => markRowFieldAsTouched(index, 'quantity')}
                  />
                </Grid>
                <Grid item xs={3.8}>
                  <FloatInput
                    name={`price-${index}`}
                    value={row.price || ''}
                    onChange={(e) => handleRowChange(index, 'price', e.target.value)}
                    placeholder="Enter price"
                    label="Price"
                    maxValue={150}
                    sx={{
                      ...inputStyle,
                      textDecoration: isPendingDelete ? 'line-through' : 'none',
                      textDecorationColor: isPendingDelete ? tokens.palette.danger : undefined,
                      textDecorationThickness: isPendingDelete ? '3px' : undefined,
                    }}
                    disabled={isLoading || !canEditRow || isPendingDelete || isFormLocked}
                    error={!isPendingDelete && shouldShowRowError(index, 'price')}
                    helperText={isPendingDelete ? '' : getRowFieldErrorMessage(index, 'price')}
                    onBlur={() => markRowFieldAsTouched(index, 'price')}
                  />
                </Grid>
                <Grid item xs={0.6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '56px' }}>
                  {showDeleteButton(index) && (
                    isPendingDelete ? (
                      <IconButton
                        size="small"
                        onClick={() => handleRowDelete(index)}
                        disabled={isLoading}
                        sx={{ padding: '8px' }}
                        aria-label="Undo delete"
                      >
                        <UndoIcon sx={{ color: tokens.palette.primary, opacity: 1 }} />
                      </IconButton>
                    ) : (
                      <IconButton 
                        color="error"
                        size="small"
                        onClick={() => handleRowDelete(index)}
                        disabled={isLoading || isFormLocked}
                        sx={{ padding: '8px' }}
                        aria-label="Delete"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    )
                  )}
                </Grid>
              </Grid>
            );
          })}
        </Box>

        {/* Sticky Add Row Button */}
        <Box sx={{ 
          flexShrink: 0,
          px: 3,
          py: 2,
          backgroundColor: tokens.colors.bodyBackgroundColor
        }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box
                onClick={() => {
                  if (!isLoading && !isFormLocked) {
                    addRow();
                  }
                }}
                sx={{
                  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                  height: '56px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: tokens.colors.defaultGridHoverColor,
                    borderColor: tokens.colors.bodyBorderColor50,
                  },
                  ...(isLoading || isFormLocked
                    ? { opacity: 0.5, pointerEvents: 'none' }
                    : {})
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: tokens.palette.primary
                }}>
                  <AddIcon sx={{ fontSize: '20px', color: tokens.palette.primary }} />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: tokens.palette.primary,
                      fontWeight: 'medium',
                      fontSize: '14px'
                    }}
                  >
                    Add Position
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      )}
    </Box>
  );
};

export default AddAccountFormContent;